<template>
  <div class="notice-list-container">
    <div class="jc-page-top">
      <div class="instance-list">
        <div class="jc-page-header">
          <h1>
            <i class="jc-icon-arrow-left left-icon" @click="goHome"></i
            >{{ $t(config.listTitleKey) }}
          </h1>
        </div>
        <div class="jc-page-content" v-loading="loading">
          <div class="notice-list">
            <div class="list-content">
              <div
                v-for="(item, index) in normalizedList"
                :key="index"
                class="list-item"
                @click="handleDetail(item)"
              >
                <el-tooltip
                  class="item-title-tooltip"
                  effect="dark"
                  :content="item.title"
                  placement="top"
                  popper-class="item-title-tooltip"
                  :disabled="!(item.title && item.title.length > 70)"
                >
                  <div class="item-title">{{ item.title }}</div>
                </el-tooltip>
                <div class="item-date">{{ item.createTime }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="el-page-footer block el-container-table-actionBar"
      v-if="normalizedList.length">
      <div></div>

      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="pageSizeArr"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="totalCount">
      </el-pagination>
    </div>
  </div>
</template>

<script>
import { getNoticeConfig } from './noticeConfig';

/**
 * 公告列表页（UI 展示层）
 *
 * 架构说明：
 *   - 通过路由 query.noticeType 确定当前公告类型，默认为 'announcement'
 *   - 由 noticeConfig.js（数据处理层）提供接口方法、字段适配、路由信息
 *   - UI 层只消费标准化的 NoticeItem 格式（title / instId / createTime）
 *   - 列表页返回首页（goHome）、跳转详情（handleDetail）均使用配置中的路由信息
 */
export default {
  name: 'NoticeList',
  data() {
    return {
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      pageSizeArr: [20, 50, 100],
      rawList: [],
      loading: false,
    };
  },
  computed: {
    /**
     * 从路由 query 中读取通知类型，获取对应配置（数据处理层）
     */
    config() {
      return getNoticeConfig(this.$route.query.noticeType);
    },
    /**
     * 经数据处理层适配后的标准化列表
     */
    normalizedList() {
      return this.config.normalizeList(this.rawList);
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    /**
     * 返回首页，使用配置中的 backRouteName
     */
    goHome() {
      this.$router.replace({ name: this.config.backRouteName });
    },
    /**
     * 获取列表数据
     * 通过配置的 listApiMethod 动态调用接口
     */
    fetchList() {
      const apiFn = this.$api[this.config.listApiMethod];
      if (typeof apiFn !== 'function') {
        console.error('[NoticeList] 接口方法不存在: ' + this.config.listApiMethod);
        return;
      }
      this.loading = true;
      const params = {
        pageSize: this.pageSize,
        pageNumber: this.currentPage,
      };
      apiFn(params)
        .then(res => {
          if (res.errno === 10000 && res.data) {
            const { list, totalCount } = this.config.extractListData(res.data);
            this.rawList = list;
            this.totalCount = totalCount;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.fetchList();
    },
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchList();
    },
    /**
     * 跳转详情页，携带 noticeType 和 instId
     */
    handleDetail(item) {
      this.$router.push({
        path: this.config.detailRoute,
        query: {
          instId: item.instId,
          noticeType: this.config.type,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../workSheet/scss/mixins';

$pagesColor: #9d9d9d;
.notice-list-container {
  background-color: #f6fafd;
  height: 100%;
  overflow: auto;
  .pages {
    i { color: $pagesColor; }
    a.firstpage i,
    a.lastpage i { background: transparent; }
  }
  .jc-table__column-filter-trigger { line-height: 20px !important; }
  .jc-table .cell { text-overflow: unset; }
}

.el-table-actionBar-item { float: left; }

// header spacing adjustments
.jc-page-header h1 {
  display: flex;
  align-items: center;
  font-size: 18px;
}
.jc-page-header .jc-box-action { margin-left: auto; }

// small icon button normalize
.el-button-icon {
  padding: 8px;
  min-width: 32px;
}

// table container adjustments
.el-container-table-actionBar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.el-pagination {
    top: 0 !important;
}
/deep/ .el-pagination__editor {
  line-height: inherit!important;
}
.notice-list {
  background: #fff;
  padding: 24px;
}
.list-content {
  .list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 0.06px solid rgba(0, 0, 0, 0.35);
    padding-bottom: 24px;
    cursor: pointer;
    .item-title {
      display: block;
      font-size: 16px;
      color: rgba(0, 0, 0, 0.85);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding-left: 15px;
      &:hover {
        color: #1D41FF;
      }
    }
    .item-date {
      width: 170px;
      flex-shrink: 0;
      text-align: right;
      font-size: 14px;
      color: rgba(0, 0, 0, 0.45);
      padding-right: 15px;
    }
  }
  .list-item + .list-item {
    padding: 24px 0;
  }
}
.left-icon {
  cursor: pointer;
}
.jc-page-header {
  height: 45px !important;
  line-height: 45px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/notice/List.vue